import { create } from 'zustand';
import { getTimeClockAdmin } from '@/api/generated/time-clock-admin/time-clock-admin';
import type {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyAttendanceDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput,
} from '@/api/generated/models';
import { useToastStore } from './useToastStore';
import { isoDate } from '@/utils/dateUtils';

const adminApi = getTimeClockAdmin();

interface SelectedMonth {
  year: number;
  month: number;
}

interface TimeclockAdminState {
  dailyStatus: FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyAttendanceDto | null;
  dailyDate: string;
  dailyLoading: boolean;
  dailyError: string | null;

  selectedEmployeeId: string | null;
  selectedMonth: SelectedMonth;
  employeeHistory: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto[];
  historyLoading: boolean;
  historyError: string | null;

  fetchDailyStatus: (date: string) => Promise<void>;
  setDailyDate: (date: string) => void;

  setSelectedEmployee: (employeeId: string | null) => void;
  setSelectedMonth: (month: SelectedMonth) => void;
  fetchEmployeeHistory: (employeeId: string, year: number, month: number) => Promise<void>;

  fetchEmployeeDay: (
    employeeId: string,
    date: string,
  ) => Promise<FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto | null>;

  correctDay: (
    employeeId: string,
    date: string,
    events: FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventInput[],
    correctionNote: string,
  ) => Promise<void>;
}

function todayDateString(): string {
  return isoDate(new Date());
}

function getMonthRange(year: number, month: number) {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

export const useTimeclockAdminStore = create<TimeclockAdminState>((set, get) => {
  const now = new Date();
  return {
    dailyStatus: null,
    dailyDate: todayDateString(),
    dailyLoading: false,
    dailyError: null,

    selectedEmployeeId: null,
    selectedMonth: { year: now.getFullYear(), month: now.getMonth() + 1 },
    employeeHistory: [],
    historyLoading: false,
    historyError: null,

    fetchDailyStatus: async (date: string) => {
      set({ dailyLoading: true, dailyError: null });
      try {
        const result = await adminApi.getApiV1TimeclockAdminDailyStatus({ date });
        set({ dailyStatus: result, dailyLoading: false });
      } catch {
        set({ dailyError: 'timeclock:errors.fetchDailyFailed', dailyLoading: false });
      }
    },

    setDailyDate: (date: string) => {
      set({ dailyDate: date });
      get().fetchDailyStatus(date);
    },

    setSelectedEmployee: (employeeId: string | null) => {
      set({ selectedEmployeeId: employeeId });
      if (employeeId) {
        const { year, month } = get().selectedMonth;
        get().fetchEmployeeHistory(employeeId, year, month);
      } else {
        set({ employeeHistory: [] });
      }
    },

    setSelectedMonth: (month: SelectedMonth) => {
      set({ selectedMonth: month });
      const employeeId = get().selectedEmployeeId;
      if (employeeId) {
        get().fetchEmployeeHistory(employeeId, month.year, month.month);
      }
    },

    fetchEmployeeHistory: async (employeeId: string, year: number, month: number) => {
      set({ historyLoading: true, historyError: null });
      try {
        const { from, to } = getMonthRange(year, month);
        const result = await adminApi.getApiV1TimeclockAdminEmployeesEmployeeIdHistory(
          employeeId,
          { from, to },
        );
        set({ employeeHistory: result ?? [], historyLoading: false });
      } catch {
        set({ historyError: 'timeclock:errors.fetchFailed', historyLoading: false });
      }
    },

    fetchEmployeeDay: async (employeeId: string, date: string) => {
      try {
        const result = await adminApi.getApiV1TimeclockAdminOverview({
          from: date,
          to: date,
          employeeId,
        });
        return result?.[0] ?? null;
      } catch {
        return null;
      }
    },

    correctDay: async (employeeId, date, events, correctionNote) => {
      set({ historyLoading: true });
      try {
        await adminApi.postApiV1TimeclockAdminEmployeesEmployeeIdDaysDateCorrect(
          employeeId,
          date,
          {
            date,
            targetEmployeeId: employeeId,
            correctedEvents: events,
            correctionNote,
          },
        );
        useToastStore.getState().addToast('Tag erfolgreich korrigiert', 'success');

        // Refresh both views
        const { year, month } = get().selectedMonth;
        await Promise.all([
          get().fetchEmployeeHistory(employeeId, year, month),
          get().fetchDailyStatus(get().dailyDate),
        ]);
      } catch (error) {
        // The dialog shows the reason and stays open; a toast here would say less.
        set({ historyLoading: false });
        throw error;
      }
    },
  };
});
